import { Link } from '@tanstack/react-router';
import { AlertTriangle, Expand, Eye, Images, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { ImageViewer } from '~/components/common/image-viewer/image-viewer';
import { Button } from '~/components/ui/button';
import {
  EnrichmentFailedBadge,
  RecuratedSinceChip,
} from '~/features/catalog-operations/components/chips';
import {
  scoreBadge,
  scoreTone,
  type ProductScoreBadge,
} from '~/features/catalog-operations/components/product-score';
import {
  isRecuratedSince,
  type Product,
} from '~/features/catalog-operations/types/catalog-operations.types';
import {
  usesNeedsAttentionDetailRoute,
  type ProductLinkContext,
} from '~/features/catalog-operations/utils/product-detail-link';
import { cn } from '~/utils/cn';

/** Tile density — the minimum column width drives how many fit per row. */
export type GridDensity = 'compact' | 'comfort' | 'spacious';

const MIN_COLUMN: Record<GridDensity, string> = {
  compact: '9.75rem',
  comfort: '13rem',
  spacious: '17.5rem',
};

/** Size tokens in ascending order, so a size array can be shown as a range.
 *  Anything outside this list falls back to a bare count. */
const SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  'XXXL',
  '0X',
  '1X',
  '2X',
  '3X',
  '4X',
  '2Y',
  '4Y',
  '6Y',
  '8Y',
  '10Y',
  '12Y',
  '14Y',
];

/**
 * Score badge with a trading-ticker lift indicator: the score, then the change
 * against the pre-enrichment baseline as a signed delta with a trend arrow —
 * green rising, red falling.
 *
 * A delta that rounds to zero renders as a flat dash rather than "+0": enrichment
 * that moved the score by a fraction of a point did not meaningfully improve the
 * product, and "+0" reads as a bug.
 */
function ScoreBadge({ badge, testId }: { badge: ProductScoreBadge; testId?: string }) {
  const rounded = Math.round(badge.value);
  const delta = badge.delta == null ? null : Math.round(badge.delta);
  const rising = delta != null && delta > 0;
  const falling = delta != null && delta < 0;
  const Arrow = rising ? TrendingUp : falling ? TrendingDown : Minus;

  return (
    <span
      data-testid={testId}
      className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-1.5 py-0.5 shadow-sm"
      title={
        delta == null
          ? badge.label
          : `${badge.label}: ${String(rounded)}/100 (${delta > 0 ? '+' : ''}${String(delta)} vs pre-enrichment)`
      }
    >
      <span className={cn('text-[11px] font-bold tabular-nums', scoreTone(badge.value))}>
        {rounded}
        <span className="text-muted-foreground text-[9px] font-semibold">/100</span>
      </span>
      {delta != null && (
        <span
          className={cn(
            'inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums',
            rising ? 'text-success' : falling ? 'text-error' : 'text-muted-foreground',
          )}
        >
          <Arrow className="size-2.5" aria-hidden="true" />
          {rising && '+'}
          {delta === 0 ? '' : delta}
        </span>
      )}
    </span>
  );
}

/** Gender for the tile chip. Title-cased rather than the API's `FEMALE`, since a
 *  shouty all-caps chip fights the rest of the caption. */
function genderChip(gender: string): string {
  const value = gender.trim();
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** Age group only — gender moved to its own chip, and repeating it in the brand
 *  line would render the same fact twice on one tile. `adult` is the default and
 *  says nothing, so only a non-adult group is worth the space. */
function audienceLabel(product: Product): string | null {
  const age = product.ageGroup?.toLowerCase();
  if (!age || age === 'adult') return null;
  return age.charAt(0).toUpperCase() + age.slice(1);
}

/** Sizes as a span plus a count. A list of 8–12 values consumes the whole tile. */
function sizeLabel(sizes: string[] | undefined): string | null {
  if (!sizes || sizes.length === 0) return null;
  const known = sizes.filter((s) => SIZE_ORDER.includes(s.toUpperCase()));
  if (known.length < 2) return `${String(sizes.length)} ${sizes.length === 1 ? 'size' : 'sizes'}`;
  const sorted = [...known].sort(
    (a, b) => SIZE_ORDER.indexOf(a.toUpperCase()) - SIZE_ORDER.indexOf(b.toUpperCase()),
  );
  return `${sorted[0] ?? ''}–${sorted[sorted.length - 1] ?? ''} · ${String(sizes.length)}`;
}

function Chip({ children, tone }: { children: React.ReactNode; tone?: 'new' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap',
        tone === 'new'
          ? 'bg-selected-row text-primary border-transparent'
          : 'bg-surface-blue text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}

interface Props {
  products: Product[];
  density: GridDensity;
  /** Appended to each tile's link so the detail breadcrumb can return here. */
  linkSearch?: Record<string, unknown>;
  /** Referrer context for the tile links. `needs-attention` routes tiles to the
   *  cross-batch detail URL, since those products carry no batch. */
  productLinkContext?: ProductLinkContext | undefined;
  /** Shows the flag reason instead of the spec chips — the needs-attention tab. */
  showAttentionReason?: boolean;
  /** Opens the quick-view dialog for a product. Omit to hide the eye control. */
  onQuickView?: ((product: Product) => void) | undefined;
  /** Opens a product in place instead of navigating to its detail route. Supplied
   *  by hosts with no catalog-operations route to navigate to — the content
   *  enrichment funnel opens the detail view in a modal so the operator stays in
   *  the wizard. Omit to keep the default route link. */
  onOpenProduct?: ((product: Product) => void) | undefined;
}

/**
 * A tile's clickable region. Renders the usual detail-route link, or a button
 * calling `onOpenProduct` when the host handles opening itself — so both hosts
 * share one tile rather than forking the grid.
 */
function OpenProductTarget({
  product,
  linkSearch,
  productLinkContext,
  onOpenProduct,
  className,
  title,
  ariaLabel,
  testId,
  children,
}: {
  product: Product;
  linkSearch?: Record<string, unknown> | undefined;
  productLinkContext?: ProductLinkContext | undefined;
  onOpenProduct?: ((product: Product) => void) | undefined;
  className: string;
  title?: string | undefined;
  ariaLabel?: string | undefined;
  /** Automation hook: `data-testid` on the rendered link or button. */
  testId?: string;
  children: ReactNode;
}) {
  if (onOpenProduct) {
    return (
      <Button
        data-testid={testId}
        type="button"
        variant="ghost"
        onClick={() => {
          onOpenProduct(product);
        }}
        {...(title === undefined ? {} : { title })}
        {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
        // Resets the Button chrome so the tile looks identical either way.
        className={cn('h-auto justify-start rounded-none bg-transparent p-0 text-left', className)}
      >
        {children}
      </Button>
    );
  }
  // Cross-batch needs-attention tiles have no batch to interpolate, so they take
  // the dedicated detail URL — the batch route would render `/catalog-operations//…`.
  if (usesNeedsAttentionDetailRoute(product, productLinkContext)) {
    return (
      <Link
        data-testid={testId}
        to="/catalog-operations/products-need-attention/$productId"
        params={{ productId: product.id }}
        {...(title === undefined ? {} : { title })}
        {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
        className={className}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      data-testid={testId}
      to="/catalog-operations/$batchId/products/$productId"
      params={{ batchId: product.batchId, productId: product.id }}
      {...(linkSearch ? { search: linkSearch } : {})}
      {...(title === undefined ? {} : { title })}
      {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
      className={className}
    >
      {children}
    </Link>
  );
}

/**
 * Grid of product tiles: photo first, then the identity a reviewer needs to place
 * the row — brand, audience, size span, colour — and the completeness score, which
 * is what makes a grid actionable rather than a picture wall. The magnifier opens
 * the full image viewer without navigating away from the list.
 */
export function ProductsGridView({
  products,
  density,
  linkSearch,
  productLinkContext,
  showAttentionReason = false,
  onQuickView,
  onOpenProduct,
}: Props) {
  // Which product's images are open in the viewer; null = closed.
  const [viewerFor, setViewerFor] = useState<Product | null>(null);

  return (
    <>
      <div
        data-testid="products-grid"
        className="grid gap-3.5 p-4"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_COLUMN[density]}, 1fr))` }}
      >
        {products.map((product) => {
          const audience = audienceLabel(product);
          const sizes = sizeLabel(product.sizes);
          const badge = scoreBadge(product);

          return (
            <div
              key={product.id}
              data-testid={`products-card-${product.id}`}
              className="group hover:border-primary relative flex flex-col overflow-hidden rounded-xl border bg-white transition-colors hover:shadow-md"
            >
              <div className="relative aspect-3/4 bg-neutral-100">
                <OpenProductTarget
                  product={product}
                  linkSearch={linkSearch}
                  productLinkContext={productLinkContext}
                  onOpenProduct={onOpenProduct}
                  ariaLabel={product.title}
                  testId={`products-card-${product.id}-image-link`}
                  className="block h-full w-full cursor-pointer"
                >
                  <CachedImage src={product.imageUrl ?? null} alt="" className="h-full w-full" />
                </OpenProductTarget>

                {badge && <ScoreBadge badge={badge} testId={`products-card-${product.id}-score`} />}

                {product.imageUrl && (product.imageCount ?? 0) > 1 && (
                  <span
                    data-testid={`products-card-${product.id}-image-count`}
                    className="absolute right-2 bottom-2 inline-flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums"
                  >
                    <Images className="size-3" aria-hidden="true" />
                    {product.imageCount}
                  </span>
                )}

                {/* Bottom-left overlay stack: a tile can be both image-less and
                    failed, so the two pills share a row rather than colliding at
                    the same absolute corner. `isEnriched === false` is strict —
                    `undefined` means the enrichment-status call hasn't landed. */}
                {(!product.imageUrl ||
                  product.isEnriched === false ||
                  isRecuratedSince(product)) && (
                  <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
                    {!product.imageUrl && (
                      <span
                        data-testid={`products-card-${product.id}-no-image-badge`}
                        className="bg-warning-50 text-warning rounded-full px-2 py-0.5 text-[10px] font-bold"
                      >
                        No image
                      </span>
                    )}
                    {product.isEnriched === false && <EnrichmentFailedBadge />}
                    {/* Same staleness signal as the list view, so the two never
                        disagree about which rows a run saw stale data for. */}
                    <RecuratedSinceChip product={product} />
                  </div>
                )}

                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  {onQuickView && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      data-testid={`products-card-${product.id}-quick-view-button`}
                      aria-label={`Quick view ${product.title}`}
                      title="Quick view"
                      onClick={() => {
                        onQuickView(product);
                      }}
                      className="text-foreground size-7 cursor-pointer rounded-md border-0 bg-white/95 shadow-sm"
                    >
                      <Eye className="size-3.5" />
                    </Button>
                  )}
                  {product.imageUrl && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      data-testid={`products-card-${product.id}-image-viewer-button`}
                      aria-label={`View images for ${product.title}`}
                      title="Open image viewer"
                      onClick={() => {
                        setViewerFor(product);
                      }}
                      className="text-foreground size-7 cursor-pointer rounded-md border-0 bg-white/95 shadow-sm"
                    >
                      <Expand className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-1.5 px-3 pt-2.5 pb-3">
                <div className="flex min-w-0 items-baseline gap-2">
                  <OpenProductTarget
                    product={product}
                    linkSearch={linkSearch}
                    productLinkContext={productLinkContext}
                    onOpenProduct={onOpenProduct}
                    title={product.title}
                    testId={`products-card-${product.id}-title-link`}
                    className="line-clamp-2 min-w-0 flex-1 cursor-pointer text-[13px] leading-snug font-semibold hover:underline"
                  >
                    {product.title}
                  </OpenProductTarget>
                </div>

                <p
                  data-testid={`products-card-${product.id}-brand`}
                  className="text-muted-foreground truncate text-[11px]"
                >
                  {[product.brand, audience].filter(Boolean).join(' · ')}
                </p>

                {showAttentionReason && product.needsAttentionReason ? (
                  <p
                    data-testid={`products-card-${product.id}-attention-reason`}
                    className="text-error flex items-start gap-1 text-[11px] leading-snug"
                  >
                    <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                    {product.needsAttentionReason}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {product.color && <Chip>{product.color}</Chip>}
                    {sizes && <Chip>{sizes}</Chip>}
                    {density === 'spacious' && product.material && <Chip>{product.material}</Chip>}
                    {product.gender && <Chip tone="new">{genderChip(product.gender)}</Chip>}
                  </div>
                )}

                <span
                  data-testid={`products-card-${product.id}-source-id`}
                  className="text-muted-foreground truncate font-mono text-[10px]"
                >
                  {product.sourceProductId}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* One viewer for the whole grid — mounting it per tile would build a dialog
          for every product on the page. */}
      <ImageViewer
        images={viewerFor?.imageUrl ? [viewerFor.imageUrl] : []}
        index={0}
        onIndexChange={() => {
          // Single image per list row: the list payload carries only the primary.
        }}
        open={viewerFor !== null}
        onOpenChange={(next) => {
          if (!next) setViewerFor(null);
        }}
        title={viewerFor?.title}
      />
    </>
  );
}
